import firebase from 'firebase/compat/app';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';

import {
  BULK_SEND_NOTIFIER_LIMIT,
  BULK_SEND_ORDER_FIELD,
  USER_TASKS_COLLECTION,
} from '../../constants/bulkSendManager';
import { buildBulkSendsScopeClauses } from '../../containers/BulkSendManagerContainer/Filters/buildBulkSendsQuery';
import { selectUserUid } from '../../store/selectors/auth';
import { selectCurrentOrganizationId } from '../../store/selectors/organizations';
import { IUserTaskDoc } from '../../types/userTask';
import {
  detectBulkSendTransitions,
  TBulkSendStatusByUid,
  toStatusByUid,
  toStatusSnapshot,
} from './detectBulkSendTransitions';
import { notifyBulkSendTransition } from './notifyBulkSendTransition';

export const useBulkSendStatusNotifier = (): void => {
  const firebaseInstance = useFirebase();
  const userId = useSelector(selectUserUid);
  const orgId = useSelector(selectCurrentOrganizationId);

  useEffect(() => {
    if (!userId || !orgId) {
      return undefined;
    }

    let known: TBulkSendStatusByUid | null = null;

    const scoped = buildBulkSendsScopeClauses({ userId, orgId }).reduce(
      (acc, clause) => acc.where(clause.field, clause.op, clause.value),
      firebaseInstance.firestore().collection(USER_TASKS_COLLECTION) as firebase.firestore.Query,
    );

    return scoped
      .orderBy(BULK_SEND_ORDER_FIELD, 'desc')
      .limit(BULK_SEND_NOTIFIER_LIMIT)
      .onSnapshot(
        (snapshot) => {
          const current = snapshot.docs.map((item) => toStatusSnapshot(item.id, item.data() as IUserTaskDoc));

          if (known) {
            detectBulkSendTransitions(known, current).forEach(notifyBulkSendTransition);
          }

          known = toStatusByUid(current);
        },
        (error: Error) => {
          console.error('[useBulkSendStatusNotifier] Firestore query failed', error);
        },
      );
  }, [firebaseInstance, userId, orgId]);
};
